// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useRef, useCallback } from "react";

export const AuthContext = createContext(null);

const API_BASE             = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const DEFAULT_IDLE_TIMEOUT = 30 * 60 * 1000;  // 30 min fallback
const REFRESH_INTERVAL     = 14 * 60 * 1000;  // 14 min (access token TTL is 15 min)

export function AuthProvider({ children }) {
  const [token,        setToken]        = useState(null);
  const [user,         setUser]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [idleTimeout,  setIdleTimeout]  = useState(DEFAULT_IDLE_TIMEOUT);

  const refreshTimerRef    = useRef(null);
  const inactivityTimerRef = useRef(null);

  // ─────────────────────────────────────────────
  // INTERNAL HELPERS
  // ─────────────────────────────────────────────

  const _applySession = (jwt, userObj) => {
    setToken(jwt);
    setUser(userObj);
    localStorage.setItem("token",               jwt);
    localStorage.setItem("user",                JSON.stringify(userObj));
    localStorage.setItem("session.initialized", "true");
  };

  const _clearSession = useCallback(() => {
    // Capture user_id + token before clearing — both are needed below.
    let _uid = null;
    try {
      const _u = JSON.parse(localStorage.getItem("user") || "null");
      _uid = _u?.id || null;
    } catch {}
    const _token = localStorage.getItem("token");

    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("session.initialized");
    // Org-modules cache is tenant-scoped — must not survive logout, or
    // the next user on a shared machine briefly sees the previous org's
    // enabled-module list (see hooks/useOrgModules.js).
    localStorage.removeItem("org_modules");
    localStorage.removeItem("org_modules_ts");
    if (_uid) {
      // K.I.R.A history + (forward-compat) per-user session id.
      try { localStorage.removeItem(`kira_history_${_uid}`); } catch {}
      try { localStorage.removeItem(`kira_session_${_uid}`); } catch {}
    }

    // Fire-and-forget: tell the backend to wipe Redis K.I.R.A history so the
    // next login doesn't pick up mid-conversation context.
    if (_token) {
      fetch(`${API_BASE}/api/auth/clear-history`, {
        method:  "POST",
        headers: { "Authorization": `Bearer ${_token}` },
      }).catch(() => {});
    }

    if (refreshTimerRef.current)     clearInterval(refreshTimerRef.current);
    if (inactivityTimerRef.current)  clearInterval(inactivityTimerRef.current);
  }, []);

  // ─────────────────────────────────────────────
  // SILENT REFRESH
  // ─────────────────────────────────────────────

  const silentRefresh = useCallback(async () => {
    try {
      // Cross-origin HttpOnly cookies can be blocked (SameSite=None+Secure on
      // some browsers / 3P-cookie phaseout), so also send the stored refresh
      // token in the request body as a fallback.
      const storedRefresh = (() => {
        try { return localStorage.getItem("refresh_token"); } catch { return null; }
      })();

      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method:      "POST",
        credentials: "include",  // sends HttpOnly refresh_token cookie automatically
        headers:     { "Content-Type": "application/json" },
        body:        JSON.stringify({ refresh_token: storedRefresh }),
      });

      if (!res.ok) {
        _clearSession();
        window.location.href = "/login";
        return null;
      }

      const data = await res.json();
      if (data.token && data.user) {
        _applySession(data.token, data.user);
        // Rotate the stored refresh token if a fresh one was returned.
        if (data.refresh_token) {
          try { localStorage.setItem("refresh_token", data.refresh_token); } catch {}
        }
        return data.token;
      }
    } catch {
      // Network error — don't force logout, retry next interval
    }
    return null;
  }, [_clearSession]);

  // ─────────────────────────────────────────────
  // SINGLE-FLIGHT LOCK
  // The backend now blacklists refresh tokens on use. If two callers
  // (the 401 interceptor + the 14-min interval, or two 401s racing on
  // dashboard mount) hit /refresh with the same stored token in parallel,
  // the first succeeds and the rest get "Refresh token has been revoked"
  // → forced logout. This lock ensures at most one /refresh is in flight;
  // concurrent callers await the same promise.
  //
  // Stored on window._ (like _originalFetch below) so HMR / component
  // remount doesn't drop the in-flight promise mid-refresh.
  const refreshOnce = useCallback(() => {
    if (!window._kweryRefreshPromise) {
      window._kweryRefreshPromise = silentRefresh().finally(() => {
        window._kweryRefreshPromise = null;
      });
    }
    return window._kweryRefreshPromise;
  }, [silentRefresh]);

  // ─────────────────────────────────────────────
  // INIT — restore session on page load
  // ─────────────────────────────────────────────

  useEffect(() => {
    const initFlag   = localStorage.getItem("session.initialized");
    const savedToken = localStorage.getItem("token");
    const savedUser  = localStorage.getItem("user");

    if (!initFlag || !savedToken || !savedUser) {
      _clearSession();
      setLoading(false);
      return;
    }

    try {
      const parsedUser = JSON.parse(savedUser);
      setToken(savedToken);
      setUser(parsedUser);
      // Fetch server session config
      fetch(`${API_BASE}/api/auth/session-config`)
        .then(r => r.json())
        .then(cfg => { if (cfg?.idle_timeout_ms) setIdleTimeout(cfg.idle_timeout_ms); })
        .catch(() => {});
      // Attempt silent refresh immediately on load to get fresh token —
      // BUT skip if we just landed here from a fresh login (token is brand
      // new, no refresh needed; running it now risks an unnecessary 401
      // that would yank the user back to /login).
      const justLoggedIn = sessionStorage.getItem("kira_from_login");
      if (!justLoggedIn) {
        refreshOnce();
      }
      sessionStorage.removeItem("kira_from_login");
    } catch {
      _clearSession();
    }

    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────
  // REFRESH TIMER
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    refreshTimerRef.current = setInterval(refreshOnce, REFRESH_INTERVAL);
    return () => clearInterval(refreshTimerRef.current);
  }, [token, refreshOnce]);

  // ─────────────────────────────────────────────
  // INACTIVITY WATCHER
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;

    const updateActivity = () => setLastActivity(Date.now());
    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown",   updateActivity);
    window.addEventListener("click",     updateActivity);
    window.addEventListener("scroll",    updateActivity);

    inactivityTimerRef.current = setInterval(() => {
      if (Date.now() - lastActivity > idleTimeout) {
        console.warn("Auto-logout: inactivity timeout.");
        logout();
        window.location.href = "/login";
      }
    }, 60_000);

    return () => {
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown",   updateActivity);
      window.removeEventListener("click",     updateActivity);
      window.removeEventListener("scroll",    updateActivity);
      clearInterval(inactivityTimerRef.current);
    };
  }, [token, lastActivity, idleTimeout]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────
  // 401 INTERCEPTOR
  // Any fetch that returns 401 automatically tries a silent refresh
  // and retries the original request with the new token.
  // ─────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;

    const originalFetch = window._originalFetch || window.fetch;
    window._originalFetch = originalFetch;

    const isOwnApi = (rawUrl) => {
      const s = String(rawUrl);
      // Relative paths are always our own API (dev proxy routes them to API_BASE).
      if (s.startsWith("/")) return true;
      try { return new URL(s).origin === new URL(API_BASE, window.location.origin).origin; }
      catch { return false; }
    };

    window.fetch = async (...args) => {
      const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
      // Pass third-party requests through untouched — never send our JWT externally.
      if (!isOwnApi(url)) return originalFetch(...args);
      // Skip the refresh endpoint itself to avoid infinite loops
      if (url.includes("/api/auth/")) {
        return originalFetch(...args);
      }

      let res = await originalFetch(...args);

      if (res.status === 401) {
        const newToken = await refreshOnce();

        if (newToken) {
          // Retry original request with new access token
          const [reqUrl, options = {}] = args;
          res = await originalFetch(reqUrl, {
            ...options,
            headers: {
              ...(options.headers || {}),
              Authorization: `Bearer ${newToken}`,
            },
          });
        }
      }

      return res;
    };

    return () => {
      if (window._originalFetch) {
        window.fetch = window._originalFetch;
      }
    };
  }, [token, refreshOnce]);

  // ─────────────────────────────────────────────
  // PUBLIC API
  // ─────────────────────────────────────────────

  const login = (jwt, userObj, refreshTokenValue = null, sessionConfig = null) => {
    _applySession(jwt, userObj);
    // Persist refresh token in localStorage as a cross-origin cookie fallback —
    // app.kwery.ai → api.kwery.ai SameSite=None cookies can be blocked silently.
    if (refreshTokenValue) {
      try { localStorage.setItem("refresh_token", refreshTokenValue); } catch {}
    }
    setLastActivity(Date.now());
    // Apply server-configured session timeout
    if (sessionConfig?.idle_timeout_ms) {
      setIdleTimeout(sessionConfig.idle_timeout_ms);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method:      "POST",
        credentials: "include",
      });
    } catch {}
    _clearSession();
  };

  // Role helpers — check both primary role and roles array
  const role       = user?.role  || "staff";
  const roles      = user?.roles || [role];
  const hasRole    = (r) => r === "superadmin"
    ? role === "superadmin"
    : roles.includes(r) || role === "superadmin";

  const isStaff     = hasRole("staff");
  const isManager   = hasRole("manager");
  const isAdmin     = hasRole("admin");
  const isExecutive = hasRole("executive");
  const isSuperAdmin = role === "superadmin";

  return (
    <AuthContext.Provider value={{
      token,
      user,
      role,
      roles,
      hasRole,
      isStaff,
      isManager,
      isAdmin,
      isExecutive,
      isSuperAdmin,
      isAuthenticated: !!token,
      login,
      logout,
      silentRefresh: refreshOnce,
      loading,
      idleTimeout,
      idleTimeoutMinutes: Math.round(idleTimeout / 60000),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;